import { useState, useEffect, useRef } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import api from "../lib/api";
import Logo from "./Logo";

const NAV = [
  { label: "Úvod", to: "/" },
  { label: "Úradné správy", to: "/uradne-spravy" },
  { label: "Súťaže", to: "/sutaze" },
  { label: "Rozpisy", to: "/rozpis" },
  { label: "Rozhodcovia a delegáti", to: "/rozhodcovia" },
  { label: "Dokumenty", to: "/dokumenty" },
  { label: "Projekty", to: "/projekty" },
  { label: "Kontakt", to: "/kontakt" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [futbalnet, setFutbalnet] = useState("https://sportnet.sme.sk/futbalnet/z/obfz-levice/s/viiliga-obfz-levice/");
  const [season, setSeason] = useState("2026/2027");
  const [extraPages, setExtraPages] = useState([]);
  const moreRef = useRef(null);

  useEffect(() => {
    api.get("/contact-info")
      .then((r) => r.data?.futbalnet_url && setFutbalnet(r.data.futbalnet_url))
      .catch(() => {});
    api.get("/settings")
      .then((r) => r.data?.current_season && setSeason(r.data.current_season))
      .catch(() => {});
    api.get("/info-pages?in_menu=true")
      .then((r) => setExtraPages(r.data || []))
      .catch(() => {});
  }, []);

  // close "more" dropdown on outside click
  useEffect(() => {
    function handleClick(e) {
      if (moreRef.current && !moreRef.current.contains(e.target)) {
        setMoreOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <header data-testid="site-header" className="sticky top-0 z-50 bg-white border-b-2 border-ink-900">
      {/* Top stripe — dark green */}
      <div className="bg-green-900 text-white text-xs">
        <div className="wrap flex items-center justify-between py-2">
          <span className="mono tracking-wider opacity-80">
            OBFZ LEVICE · OFICIÁLNY WEB · EST. 1993
          </span>
          <div className="hidden md:flex items-center gap-5 mono">
            <a href="mailto:sekretar@obfz-levice.sk" className="hover:text-green-100 transition-colors">
              sekretar@obfz-levice.sk
            </a>
            <span aria-hidden="true" className="opacity-30">|</span>
            <a href="tel:0905726170" className="hover:text-green-100 transition-colors">
              0905 726 170
            </a>
          </div>
        </div>
      </div>

      {/* Main row */}
      <div className="wrap">
        <div className="flex items-center justify-between py-4">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <Logo size={68} />
            <div className="leading-none">
              <div className="font-heading font-extrabold text-ink-900 text-[15px] md:text-base tracking-tight">
                OBLASTNÝ FUTBALOVÝ ZVÄZ
              </div>
              <div className="font-heading font-extrabold text-green-700 text-xl md:text-2xl tracking-tight mt-0.5">
                LEVICE
              </div>
            </div>
          </Link>

          <div className="flex items-center gap-2">
            <a href={futbalnet} target="_blank" rel="noopener noreferrer"
               className="hidden md:inline-flex btn btn-dark" data-testid="futbalnet-button">
              Futbalnet <ArrowUpRight className="w-4 h-4" />
            </a>
            <button type="button" onClick={() => setOpen(!open)}
                    className="xl:hidden p-2.5 border-2 border-ink-900 hover:bg-ink-900 hover:text-white transition-colors"
                    aria-label="Menu" data-testid="mobile-menu-toggle">
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Nav bar */}
        <nav className="hidden xl:flex items-center justify-between border-t border-ink-100" data-testid="primary-nav">
          <div className="flex items-center">
            {NAV.map((item, i) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-${item.to.replace(/\//g, "") || "home"}`}
                className={({ isActive }) =>
                  `px-4 py-4 text-[13px] font-semibold tracking-wide uppercase transition-colors relative ${
                    isActive ? "text-green-700" : "text-ink-800 hover:text-green-700"
                  } ${i !== 0 ? "border-l border-ink-100" : ""}`
                }
              >
                {({ isActive }) => (
                  <>
                    {item.label}
                    {isActive && <span className="absolute left-0 right-0 bottom-[-2px] h-[3px] bg-green-700" />}
                  </>
                )}
              </NavLink>
            ))}

            {/* VIAC dropdown */}
            {extraPages.length > 0 && (
              <div ref={moreRef} className="relative border-l border-ink-100">
                <button
                  type="button"
                  onClick={() => setMoreOpen(!moreOpen)}
                  className={`px-4 py-4 text-[13px] font-semibold tracking-wide uppercase transition-colors flex items-center gap-1.5 ${
                    moreOpen ? "text-green-700 bg-green-50" : "text-ink-800 hover:text-green-700"
                  }`}
                  data-testid="nav-more"
                >
                  Viac
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${moreOpen ? "rotate-180" : ""}`} />
                </button>
                {moreOpen && (
                  <div
                    className="absolute right-0 top-full bg-white border-2 border-ink-900 shadow-xl w-[640px] grid grid-cols-2 gap-px bg-ink-100 z-50"
                    data-testid="more-dropdown"
                  >
                    {extraPages.map((p) => (
                      p.external_link && !p.content ? (
                        <a
                          key={p.id}
                          href={p.external_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => setMoreOpen(false)}
                          className="bg-white p-4 hover:bg-cream group flex items-start gap-3"
                          data-testid={`more-${p.slug}`}
                        >
                          <span className="mono text-[10px] font-bold text-ink-400 mt-0.5">
                            {String(p.order || 0).padStart(2, "0")}
                          </span>
                          <div className="flex-1">
                            <div className="font-heading font-bold text-ink-900 text-sm group-hover:text-green-700 leading-snug">
                              {p.title}
                            </div>
                            {p.subtitle && <div className="mono text-[10px] uppercase tracking-wider text-ink-400 mt-1">{p.subtitle}</div>}
                          </div>
                          <ArrowUpRight className="w-3.5 h-3.5 text-ink-300 group-hover:text-green-700 flex-shrink-0" />
                        </a>
                      ) : (
                        <Link
                          key={p.id}
                          to={`/info/${p.slug}`}
                          onClick={() => setMoreOpen(false)}
                          className="bg-white p-4 hover:bg-cream group flex items-start gap-3"
                          data-testid={`more-${p.slug}`}
                        >
                          <span className="mono text-[10px] font-bold text-ink-400 mt-0.5">
                            {String(p.order || 0).padStart(2, "0")}
                          </span>
                          <div className="flex-1">
                            <div className="font-heading font-bold text-ink-900 text-sm group-hover:text-green-700 leading-snug">
                              {p.title}
                            </div>
                            {p.subtitle && <div className="mono text-[10px] uppercase tracking-wider text-ink-400 mt-1">{p.subtitle}</div>}
                          </div>
                        </Link>
                      )
                    ))}
                    <Link
                      to="/struktura"
                      onClick={() => setMoreOpen(false)}
                      className="bg-ink-900 text-white p-4 hover:bg-green-700 group flex items-start gap-3 col-span-2"
                      data-testid="more-struktura"
                    >
                      <span className="mono text-[10px] font-bold text-white/60 mt-0.5">00</span>
                      <div className="flex-1">
                        <div className="font-heading font-bold text-white text-sm leading-snug">
                          Štruktúra ObFZ
                        </div>
                        <div className="mono text-[10px] uppercase tracking-wider text-white/60 mt-1">
                          Orgány a komisie zväzu
                        </div>
                      </div>
                      <ArrowUpRight className="w-3.5 h-3.5 text-white/60 flex-shrink-0" />
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>
          <span className="mono text-[11px] text-ink-500 tracking-wider pr-2">
            SEZÓNA · {season}
          </span>
        </nav>

        {/* Mobile nav */}
        {open && (
          <nav className="xl:hidden border-t border-ink-100 py-3 max-h-[80vh] overflow-y-auto" data-testid="mobile-nav">
            <div className="grid grid-cols-2 gap-1 mb-3">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `px-3 py-2.5 text-sm font-semibold uppercase tracking-wide ${
                      isActive ? "bg-green-700 text-white" : "text-ink-800 hover:bg-ink-50"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </div>
            {extraPages.length > 0 && (
              <>
                <div className="mono text-[11px] uppercase tracking-widest text-ink-400 px-3 mt-3 mb-2">Ďalšie sekcie</div>
                <div className="grid grid-cols-1 gap-1">
                  {extraPages.map((p) =>
                    p.external_link && !p.content ? (
                      <a key={p.id} href={p.external_link} target="_blank" rel="noopener noreferrer"
                         className="px-3 py-2 text-sm font-semibold text-ink-800 hover:bg-ink-50 flex items-center justify-between">
                        {p.title}<ArrowUpRight className="w-3.5 h-3.5" />
                      </a>
                    ) : (
                      <Link key={p.id} to={`/info/${p.slug}`} onClick={() => setOpen(false)}
                            className="px-3 py-2 text-sm font-semibold text-ink-800 hover:bg-ink-50">
                        {p.title}
                      </Link>
                    )
                  )}
                  <Link to="/struktura" onClick={() => setOpen(false)}
                        className="px-3 py-2 text-sm font-semibold text-ink-800 hover:bg-ink-50">
                    Štruktúra ObFZ
                  </Link>
                </div>
              </>
            )}
            <a href={futbalnet} target="_blank" rel="noopener noreferrer" className="mt-3 btn btn-dark w-full">
              Futbalnet <ArrowUpRight className="w-4 h-4" />
            </a>
          </nav>
        )}
      </div>
    </header>
  );
}
